import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { R32_MATCHES, R16_MATCHES, QF_MATCHES, SF_MATCHES } from '../data/tournament';
import FLAGS from '../data/flags';
import FlagIcon from './FlagIcon';

const BURST_ANGLES = [0, 60, 120, 180, 240, 300];

// Primary color per ISO flag code — used to paint the perimeter car
const FLAG_PRIMARY_COLORS = {
  mx: '#006847', kr: '#C60C30', za: '#007A4D', cz: '#D7141A',
  ca: '#FF0000', ch: '#FF0000', qa: '#8D1B3D', ba: '#002395',
  br: '#009C3B', ma: '#C1272D', 'gb-sct': '#005EB8', ht: '#00209F',
  us: '#B31942', py: '#D52B1E', au: '#00008B', tr: '#E30A17',
  de: '#333333', ci: '#F77F00', ec: '#FFD100', cw: '#002B7F',
  nl: '#AE1C28', se: '#006AA7', tn: '#E70013', be: '#333333',
  eg: '#CE1126', ir: '#239F40', nz: '#00247D', es: '#AA151B',
  cv: '#003893', sa: '#006C35', uy: '#5EB6E4', fr: '#002395',
  sn: '#00853F', iq: '#CE1126', no: '#EF2B2D', ar: '#74ACDF',
  dz: '#006233', at: '#ED2939', jo: '#007A3D', pt: '#006600',
  cd: '#007FFF', uz: '#1EB53A', co: '#FCD116', 'gb-eng': '#CF081F',
  hr: '#FF0000', gh: '#006B3F', pa: '#005293',
};

// ── Smoke cloud (shared by cars and trains) ──────────────────────────────────

function spawnSmoke(cx, cy, scale = 1) {
  const sz  = (0.8 + Math.random() * 0.5) * scale;
  const dur = (0.95 + Math.random() * 0.35).toFixed(2);
  const circles = [
    [  0,   0,  9],
    [  0, -13,  7],
    [ -9,  -6,  6],
    [  9,  -6,  6],
    [ -6,   8,  5],
    [  6,   8,  5],
  ];
  const wrap = document.createElement('div');
  wrap.style.cssText = [
    'position:fixed',
    `left:${cx - 26}px`,
    `top:${cy - 26}px`,
    'width:52px', 'height:52px',
    'pointer-events:none',
    'z-index:9996',
    `animation:smokeCloud ${dur}s ease-out forwards`,
  ].join(';');
  circles.forEach(([ox, oy, r]) => {
    const d = document.createElement('div');
    const size = r * 2 * sz;
    d.style.cssText = [
      'position:absolute',
      `left:${26 + (ox - r) * sz}px`,
      `top:${26 + (oy - r) * sz}px`,
      `width:${size}px`, `height:${size}px`,
      'border-radius:50%',
      'background:rgba(225,220,215,0.92)',
      'box-shadow:inset 0 -3px 6px rgba(60,60,60,0.18), 0 0 0 1.5px rgba(160,155,150,0.4)',
    ].join(';');
    wrap.appendChild(d);
  });
  document.body.appendChild(wrap);
  setTimeout(() => wrap.parentNode?.removeChild(wrap), parseFloat(dur) * 1000 + 100);
}

// ── Flag burst (existing) ────────────────────────────────────────────────────

function useFlagBurst() {
  const [particles, setParticles] = useState([]);

  const burst = useCallback((e, isoCode) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const id = Date.now();

    const newParticles = BURST_ANGLES.map((deg, i) => {
      const rad = (deg * Math.PI) / 180;
      const type = i % 2 === 0 ? 'burst' : 'drive';
      return {
        id: id + i,
        x: cx, y: cy,
        dx: Math.cos(rad), dy: Math.sin(rad),
        dir: Math.random() < 0.5 ? 1 : -1,
        type,
        isoCode,
      };
    });

    setParticles(prev => [...prev, ...newParticles]);
    setTimeout(() => setParticles(prev => prev.filter(p => p.id < id || p.id >= id + BURST_ANGLES.length)), 950);
  }, []);

  const Portal = particles.length === 0 ? null : createPortal(
    particles.map(p => (
      p.type === 'burst' ? (
        <span
          key={p.id}
          className={`flag-burst-particle fi fi-${p.isoCode || 'un'}`}
          style={{ left: p.x, top: p.y, '--dx': p.dx, '--dy': p.dy }}
        />
      ) : (
        <span
          key={p.id}
          className={`flag-drive-particle fi fi-${p.isoCode || 'un'}`}
          style={{ left: p.x, top: p.y, '--dir': p.dir }}
        />
      )
    )),
    document.body
  );

  return { burst, Portal };
}

// ── Perimeter car ────────────────────────────────────────────────────────────

function PerimeterCar({ car, onDone }) {
  const ref = useRef(null);

  useEffect(() => {
    const EDGE = 24;
    const SMOKE_INTERVAL = 380;
    let rafId;
    let lastSmoke = 0;

    const animate = () => {
      if (!ref.current) return;
      const now = Date.now();
      const w = window.innerWidth;
      const h = window.innerHeight;
      const perimeter = 2 * (w + h);
      const elapsed = now - car.startTime;
      const progress = elapsed / car.duration;

      if (progress >= 1) {
        onDone(car.id);
        return;
      }

      const traveled = progress * perimeter;
      const raw = ((car.startOffset + car.direction * traveled) % perimeter + perimeter) % perimeter;

      let x, y, angle;
      if (raw < w) {
        x = raw; y = EDGE;
        angle = car.direction > 0 ? 90 : 270;
      } else if (raw < w + h) {
        x = w - EDGE; y = raw - w;
        angle = car.direction > 0 ? 180 : 0;
      } else if (raw < 2 * w + h) {
        x = w - (raw - w - h); y = h - EDGE;
        angle = car.direction > 0 ? 270 : 90;
      } else {
        x = EDGE; y = h - (raw - 2 * w - h);
        angle = car.direction > 0 ? 0 : 180;
      }

      const opacity = progress > 0.82 ? String((1 - (progress - 0.82) / 0.18).toFixed(3)) : '1';
      ref.current.style.transform = `translate(${x}px,${y}px) translate(-50%,-50%) rotate(${angle}deg)`;
      ref.current.style.opacity = opacity;

      const onRightDown = raw >= w && raw < w + h && car.direction > 0;
      if (onRightDown && now - lastSmoke > SMOKE_INTERVAL) {
        lastSmoke = now;
        const θ = angle * Math.PI / 180;
        spawnSmoke(x - 17 * Math.sin(θ), y + 17 * Math.cos(θ));
      }

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [car, onDone]);

  const c = car.color;
  const dark = '#111827';
  const glass = 'rgba(190,230,255,0.45)';

  return createPortal(
    <div
      ref={ref}
      style={{
        position: 'fixed', top: 0, left: 0,
        pointerEvents: 'none', zIndex: 9997,
        willChange: 'transform, opacity',
      }}
    >
      <div style={{
        width: 18, height: 34,
        background: c,
        borderRadius: '5px 5px 3px 3px',
        position: 'relative',
        boxShadow: `0 0 10px ${c}bb, 0 0 3px ${c}`,
      }}>
        <div style={{ position: 'absolute', top: 5, left: 3, right: 3, height: 9, background: glass, borderRadius: '2px 2px 0 0' }} />
        <div style={{ position: 'absolute', bottom: 4, left: 3, right: 3, height: 6, background: 'rgba(190,230,255,0.2)', borderRadius: '0 0 2px 2px' }} />
        <div style={{ position: 'absolute', top: 3, left: -5, width: 5, height: 9, background: dark, borderRadius: 2 }} />
        <div style={{ position: 'absolute', top: 3, right: -5, width: 5, height: 9, background: dark, borderRadius: 2 }} />
        <div style={{ position: 'absolute', bottom: 3, left: -5, width: 5, height: 9, background: dark, borderRadius: 2 }} />
        <div style={{ position: 'absolute', bottom: 3, right: -5, width: 5, height: 9, background: dark, borderRadius: 2 }} />
      </div>
    </div>,
    document.body
  );
}

// ── Steam train (goes straight across the screen) ────────────────────────────

function SteamTrain({ train, onDone }) {
  const ref = useRef(null);

  useEffect(() => {
    const SMOKE_INTERVAL = 110;
    let rafId;
    let lastSmoke = 0;

    const animate = () => {
      if (!ref.current) return;
      const now = Date.now();
      const w = window.innerWidth;
      const elapsed = now - train.startTime;
      const progress = elapsed / train.duration;

      if (progress >= 1) {
        onDone(train.id);
        return;
      }

      // Travel from off-screen left to off-screen right (or reversed)
      const totalDist = w + 240;
      const x = train.direction === 1
        ? -120 + progress * totalDist
        : w + 120 - progress * totalDist;
      const y = train.y + Math.sin(elapsed / 70) * 4; // rattle

      const opacity = progress > 0.88 ? String((1 - (progress - 0.88) / 0.12).toFixed(3)) : '1';
      const flip = train.direction === -1 ? ' scaleX(-1)' : '';
      ref.current.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)${flip}`;
      ref.current.style.opacity = opacity;

      // Chimney is at the front of 🚂 — roughly 28px left of center, 20px above center
      if (now - lastSmoke > SMOKE_INTERVAL) {
        lastSmoke = now;
        // Chimney position in world coords (before flip — flip doesn't affect world pos calc)
        const chimneyOffX = train.direction === 1 ? -28 : 28;
        spawnSmoke(x + chimneyOffX, y - 20, 1.5);
      }

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [train, onDone]);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: 'fixed', top: 0, left: 0,
        pointerEvents: 'none', zIndex: 9998,
        willChange: 'transform, opacity',
        fontSize: '72px',
        lineHeight: 1,
        filter: `drop-shadow(0 0 18px ${train.color}) drop-shadow(0 0 6px #fff8)`,
      }}
    >
      🚂
    </div>,
    document.body
  );
}

// ── Car / train spawner ──────────────────────────────────────────────────────

function useCarOrbit() {
  const [cars, setCars] = useState([]);

  const removeCar = useCallback((id) => {
    setCars(prev => prev.filter(c => c.id !== id));
  }, []);

  const spawnCar = useCallback((isoCode) => {
    if (!isoCode || Math.random() > 0.5) return;
    const color = FLAG_PRIMARY_COLORS[isoCode] || '#f0b429';
    const id = Date.now() + Math.random();
    const isSteam = Math.random() < 0.5;

    if (isSteam) {
      setCars(prev => [...prev, {
        id,
        type: 'steam',
        color,
        startTime: Date.now(),
        duration: 1400 + Math.random() * 900,
        direction: Math.random() < 0.5 ? 1 : -1,
        y: 80 + Math.random() * (window.innerHeight - 160),
      }]);
    } else {
      setCars(prev => [...prev, {
        id,
        type: 'perimeter',
        color,
        startTime: Date.now(),
        duration: 8000 + Math.random() * 5000,
        startOffset: Math.random() * 2 * (window.innerWidth + window.innerHeight),
        direction: Math.random() < 0.5 ? 1 : -1,
      }]);
    }
  }, []);

  return { spawnCar, cars, removeCar };
}

// ── Bracket logic ────────────────────────────────────────────────────────────

function resolveSlot(slotKey, picks, teams, actualResults) {
  if (!slotKey) return null;
  const teamsByGroup = (g) => teams.filter(t => t.group_name === g);

  const groupMatch = slotKey.match(/^([12])([A-L])$/);
  if (groupMatch) {
    const pos = parseInt(groupMatch[1]) - 1;
    const g = groupMatch[2];
    if (actualResults) {
      const actualId = actualResults[`group_${g}_pos${pos + 1}`];
      if (actualId) return teams.find(t => t.id === actualId) || null;
    }
    const order = picks.groups?.[g] || teamsByGroup(g).map(t => t.id);
    const teamId = order[pos];
    return teams.find(t => t.id === teamId) || null;
  }

  // 3rd-of-group slot: e.g. "3D" = 3rd place of Group D
  const thirdGroupMatch = slotKey.match(/^3([A-L])$/);
  if (thirdGroupMatch) {
    const g = thirdGroupMatch[1];
    if (actualResults) {
      const actualId = actualResults[`third_advance_${g}`];
      if (actualId) return teams.find(t => t.id === actualId) || null;
    }
    const order = picks.groups?.[g] || teamsByGroup(g).map(t => t.id);
    const teamId = order[2];
    return teams.find(t => t.id === teamId) || null;
  }

  // Legacy ranked 3rd slot: "3rd1"–"3rd8"
  const thirdMatch = slotKey.match(/^3rd(\d)$/);
  if (thirdMatch) {
    const pos = parseInt(thirdMatch[1]) - 1;
    if (actualResults) {
      const actualId = actualResults[`third_rank_${pos + 1}`];
      if (actualId) return teams.find(t => t.id === actualId) || null;
    }
    const advancingGroups = picks.thirdPlaceAdvance || [];
    const g = advancingGroups[pos];
    if (!g) return null;
    const order = picks.groups?.[g] || teamsByGroup(g).map(t => t.id);
    const teamId = order[2];
    return teams.find(t => t.id === teamId) || null;
  }

  return null;
}

function MatchBox({ matchId, team1, team2, winner, actualWinner, onPick, locked, onBurst, onCarSpawn }) {
  const renderTeam = (team, side) => {
    const isSelected   = !!(winner && team && winner === team.id);
    const isActualWin  = !!(actualWinner && team && actualWinner === team.id);
    const isCorrect    = isSelected && isActualWin;
    const isWrong      = isSelected && !!actualWinner && !isActualWin;
    const isActualOnly = isActualWin && !isSelected;   // actual winner but not the user's pick
    const isPending    = isSelected && !actualWinner;  // user picked, result not in yet
    const isTbd        = !team;

    let rowStyle = {};
    let badge = null;

    if (isCorrect) {
      rowStyle = { background: 'rgba(34,197,94,0.22)', borderLeft: '3px solid #22c55e' };
      badge = <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#22c55e', whiteSpace: 'nowrap' }}>✓ Correct</span>;
    } else if (isWrong) {
      rowStyle = { background: 'rgba(239,68,68,0.18)', borderLeft: '3px solid #ef4444', opacity: 0.85 };
      badge = <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#ef4444', whiteSpace: 'nowrap' }}>✗ Your pick</span>;
    } else if (isActualOnly) {
      rowStyle = { background: 'rgba(240,180,41,0.18)', borderLeft: '3px solid var(--gold)' };
      badge = <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--gold)', whiteSpace: 'nowrap' }}>⚽ Actual</span>;
    } else if (isPending) {
      rowStyle = { borderLeft: '3px solid var(--accent)' };
      badge = <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Your pick</span>;
    }

    return (
      <div
        key={side}
        className={`match-team ${isSelected ? 'selected' : ''} ${isTbd ? 'tbd' : ''}`}
        style={rowStyle}
        onClick={(e) => {
          if (locked || isTbd || !team) return;
          onPick(matchId, team.id);
          onBurst(e, FLAGS[team.country_code]);
          onCarSpawn(FLAGS[team.country_code]);
        }}
      >
        {team ? <FlagIcon code={team.country_code} size="1em" /> : <span style={{ minWidth: 20 }}>?</span>}
        <span style={{ fontSize: '0.75rem', flex: 1, textDecoration: isWrong ? 'line-through' : 'none' }}>
          {team ? team.name : 'TBD'}
        </span>
        {badge}
      </div>
    );
  };
  return (
    <div className="match-box">
      {renderTeam(team1, 'top')}
      {renderTeam(team2, 'bot')}
    </div>
  );
}

function getWinner(matchId, knockout, picks, teams) {
  const teamId = knockout[matchId];
  if (!teamId) return null;
  return teams.find(t => t.id === teamId) || null;
}

// Resolves which team actually advanced from a match — uses actual result first, falls back to user's pick.
function getAdvancer(matchId, knockout, teams, actualResults) {
  const actualId = actualResults?.[matchId];
  if (actualId) return teams.find(t => t.id === actualId) || null;
  const pickedId = knockout[matchId];
  if (!pickedId) return null;
  return teams.find(t => t.id === pickedId) || null;
}

export default function KnockoutBracket({ picks, teams, knockout, onPick, locked, actualResults }) {
  const { burst, Portal: BurstPortal } = useFlagBurst();
  const { spawnCar, cars, removeCar } = useCarOrbit();

  const r32Participants = R32_MATCHES.map(m => ({
    ...m,
    team1: resolveSlot(m.slot1, picks, teams, actualResults),
    team2: resolveSlot(m.slot2, picks, teams, actualResults),
    winner: knockout[m.id] || null,
  }));

  const r16Participants = R16_MATCHES.map(m => {
    const feeders = r32Participants.filter(r => r.nextMatch === m.id);
    const top = feeders.find(f => f.nextSlot === 'top');
    const bot = feeders.find(f => f.nextSlot === 'bot');
    return {
      ...m,
      team1: top ? getAdvancer(top.id, knockout, teams, actualResults) : null,
      team2: bot ? getAdvancer(bot.id, knockout, teams, actualResults) : null,
      winner: knockout[m.id] || null,
    };
  });

  const qfParticipants = QF_MATCHES.map(m => {
    const feeders = r16Participants.filter(r => r.nextMatch === m.id);
    const top = feeders.find(f => f.nextSlot === 'top');
    const bot = feeders.find(f => f.nextSlot === 'bot');
    return {
      ...m,
      team1: top ? getAdvancer(top.id, knockout, teams, actualResults) : null,
      team2: bot ? getAdvancer(bot.id, knockout, teams, actualResults) : null,
      winner: knockout[m.id] || null,
    };
  });

  const sfParticipants = SF_MATCHES.map(m => {
    const feeders = qfParticipants.filter(r => r.nextMatch === m.id);
    const top = feeders.find(f => f.nextSlot === 'top');
    const bot = feeders.find(f => f.nextSlot === 'bot');
    return {
      ...m,
      team1: top ? getAdvancer(top.id, knockout, teams, actualResults) : null,
      team2: bot ? getAdvancer(bot.id, knockout, teams, actualResults) : null,
      winner: knockout[m.id] || null,
    };
  });

  const finalTeam1 = getAdvancer('sf_m01', knockout, teams, actualResults);
  const finalTeam2 = getAdvancer('sf_m02', knockout, teams, actualResults);
  const champion = knockout['final'] ? teams.find(t => t.id === knockout['final']) : null;

  const sf1Loser = sfParticipants[0] ? (
    sfParticipants[0].winner
      ? (sfParticipants[0].team1?.id === sfParticipants[0].winner ? sfParticipants[0].team2 : sfParticipants[0].team1)
      : null
  ) : null;
  const sf2Loser = sfParticipants[1] ? (
    sfParticipants[1].winner
      ? (sfParticipants[1].team1?.id === sfParticipants[1].winner ? sfParticipants[1].team2 : sfParticipants[1].team1)
      : null
  ) : null;

  const renderRound = (title, matches) => {
    const played  = matches.filter(m => actualResults?.[m.id]);
    const correct = played.filter(m => m.winner && actualResults[m.id] === m.winner);
    const allCorrect = played.length > 0 && correct.length === played.length;
    const scoreColor = played.length === 0 ? 'transparent'
      : allCorrect ? '#22c55e'
      : correct.length === 0 ? '#ef4444'
      : 'var(--gold)';

    return (
    <div className="bracket-round">
      <div className="round-header" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
        {title}
        {played.length > 0 && (
          <span style={{ fontSize:'0.72rem', fontWeight:700, color: scoreColor, background:'rgba(0,0,0,0.25)', borderRadius:4, padding:'1px 5px' }}>
            {correct.length}/{played.length}
          </span>
        )}
      </div>
      <div className="round-matches">
        {matches.map(m => (
          <MatchBox
            key={m.id}
            matchId={m.id}
            team1={m.team1}
            team2={m.team2}
            winner={m.winner}
            actualWinner={actualResults?.[m.id] || null}
            onPick={onPick}
            onBurst={burst}
            onCarSpawn={spawnCar}
            locked={locked}
          />
        ))}
      </div>
    </div>
  );
  };

  return (
    <div className="knockout-section">
      {BurstPortal}
      {cars.map(car =>
        car.type === 'steam'
          ? <SteamTrain key={car.id} train={car} onDone={removeCar} />
          : <PerimeterCar key={car.id} car={car} onDone={removeCar} />
      )}

      <h2>Knockout Bracket{!locked && ' — Click a team to pick the winner'}</h2>
      {!locked && (
        <p className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>
          Teams auto-fill from your group stage picks. Complete your group picks first, then pick match winners round by round.
        </p>
      )}
      <div className="knockout-wrapper" style={{ overflowX: 'auto' }}>
        <div className="bracket-rounds" style={{ minWidth: '960px' }}>
          {renderRound('Round of 32', r32Participants)}
          {renderRound('Round of 16', r16Participants)}
          {renderRound('Quarter-Finals', qfParticipants)}
          {renderRound('Semi-Finals', sfParticipants)}
          <div className="bracket-round">
            <div className="round-header">Final</div>
            <div className="round-matches">
              <MatchBox
                matchId="final"
                team1={finalTeam1}
                team2={finalTeam2}
                winner={knockout['final']}
                actualWinner={actualResults?.['final'] || null}
                onPick={onPick}
                onBurst={burst}
                onCarSpawn={spawnCar}
                locked={locked}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3" style={{ maxWidth: '320px' }}>
        <h3 style={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>3rd Place Match</h3>
        <MatchBox
          matchId="3rd_place"
          team1={sf1Loser}
          team2={sf2Loser}
          winner={knockout['3rd_place']}
          actualWinner={actualResults?.['3rd_place'] || null}
          onPick={onPick}
          onBurst={burst}
          onCarSpawn={spawnCar}
          locked={locked}
        />
      </div>

      <div className="champion-section mt-3">
        <div className="champion-trophy">🏆</div>
        <h2>World Cup 2026 Champion</h2>
        {champion ? (
          <>
            <div className="champion-name" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <FlagIcon code={champion.country_code} size="1.8rem" />
              {champion.name}
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>Your predicted champion</div>
          </>
        ) : !locked ? (
          <div className="text-muted mt-1">Pick your Final winner above to set your champion</div>
        ) : null}
      </div>
    </div>
  );
}
